import * as _4 from 'lodash4';
import { INavigationState } from '../../nav_bar_interfaces';

export class NavigationHistoryStack {
  private _navigationHistoryStack: INavigationState[] = [];
  private _shouldStateBeAdded: boolean = false;
  private _isInTransition: boolean = false;
  private _defaultState: INavigationState;

  constructor(private $timeout: ng.ITimeoutService, private $state: ng.ui.IStateService) {}

  private _addState(state: INavigationState) {
    if (_4.isEmpty(state.name)) {
      return;
    }

    const ifNoHistoryAndStateIgnored = this.isNavigationHistoryEmpty && this.isHistoryDisabled(state);
    const nextState = ifNoHistoryAndStateIgnored ? this._defaultState : state;

    this._navigationHistoryStack.push(nextState);
    return nextState;
  }

  private _deleteState() {
    this._navigationHistoryStack.pop();
  }

  private get navigationHistory(): INavigationState[] {
    return this._navigationHistoryStack;
  }

  set isTransitioning(val: boolean) {
    if (!val) {
      // this is to prevent a weird dual event firing bug from occuring when
      // a user hits the back button after the app has been activated by an
      // outside link from a deep link or push notification.
      // This ONLY occurs on back button, and the outside link function somehow
      // causes the events to fire before our app-based mitigations for dual eventing.
      this.$timeout(() => {
        this._isInTransition = false;
      }, 64);
    } else {
      this._isInTransition = val;
    }
  }

  get isNavigationHistoryEmpty() {
    return _4.isEmpty(this.navigationHistory);
  }

  set defaultStateName(state: string) {
    this._defaultState = {
      name: state
    };
  }

  changeStateStatus(fromState: INavigationState, toState: INavigationState) {
    if (!this.isHistoryDisabled(fromState)) {
      this._shouldStateBeAdded = _4.get(_4.last(this.navigationHistory), 'name') !== toState.name;
    }
  }

  changeNavigatioHistoryStack(fromState: INavigationState, toState: INavigationState) {
    if (!this.isHistoryDisabled(fromState) && this._shouldStateBeAdded) {
      this._addState(fromState);
    }
  }

  get previousState() {
    if (this._isInTransition) {
      // this is the second part, where we lie to the controllers
      // and tell them they are at the root of the stack if we are mid change
      return {} as INavigationState;
    }
    return this.__getPreviousState();
  }

  deleteAndGoBack(): void {
    this._deleteState();
    this.goBack();
  }

  /**
   * go to last state through angular routing, but use href if we have a native
   */
  goBack(): void {
    const previousState: INavigationState = this.previousState;
    if (_4.isEmpty(previousState)) {
      return;
    }

    const name = previousState.name;
    const isNative = name === 'my.native.active';
    const params: { path: string } = (previousState.params || { path: '' }) as { path: string };

    if (isNative) {
      location.href = `#/my/native/${params.path}/`;
    } else {
      this.$state.go(previousState.name, previousState.params);
    }
  }

  goBackLink(): string {
    if (this.previousState.name === 'my.ap3.active') {
      return 'my.ap3-restart';
    }
    return `${this.previousState.name}(${JSON.stringify(this.previousState.params)})`;
  }

  get goBackLinkText(): string {
    const viewTitle: string = _4.get(this.previousState, 'data.viewTitle');
    const pageTitle: string = _4.get(this.previousState, 'data.pageTitle');
    const DEFAULT_TITLE = `Go Back`;
    return viewTitle || pageTitle || DEFAULT_TITLE;
  }

  private __getPreviousState(): INavigationState {
    if (this.isNavigationHistoryEmpty) {
      return this._defaultState;
    }

    const prevState = _4.last(this.navigationHistory);
    if (prevState.name === this.$state.current.name) {
      this._deleteState();
      return this.__getPreviousState();
    } else {
      return prevState;
    }
  }

  private isHistoryDisabled(state: INavigationState): boolean {
    return _4.get(state, 'abstract', false) || _4.get(state, 'data.ignoredInHistory', false);
  }

  public clearHistory() {
    this._navigationHistoryStack = [];
    if (this.$state.current.name) {
      this._addState({
        name: this.$state.current.name,
        params: this.$state.current.params,
        data: this.$state.current.data
      });
    }
  }
}



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-navbar/src/app_nav_bar/services/navigation_history_stack.ts